/**
 * Utilidades compartidas para el módulo de Finanzas
 */

export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Extraer solo el número de factura (sin prefijo DC2025)
export const extraerNumeroFactura = (numeroCompleto) => {
  if (!numeroCompleto) return '';
  const str = String(numeroCompleto);
  const match = str.match(/DC\d{4}(\d+)$/);
  if (match) return match[1];
  return str;
};

// Función para formatear fecha a DD/MM/AAAA
export const formatFecha = (fecha) => {
  if (!fecha) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) return fecha;
  if (/^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    const [año, mes, dia] = fecha.split('-');
    return `${dia}/${mes}/${año}`;
  }
  return fecha;
};

// Quién cobra (o paga) un movimiento del banco, a partir de su concepto.
// Formato del BBVA (Enable Banking): tramos separados por "//"; quién va al
// final (sin nº de mandato SEPA, nº de tarjeta ni ciudad del datáfono) o, en
// transferencias, delante de " - TRANSFERENCIAS". El tipo de movimiento no
// interesa. Misma regla que backend/concepto_bancario.py: si se toca una, tocar la otra.
const GENERICO_BANCO = /^(PAGO CON TARJETA( EN .*)?|TRANSFERENCIAS?( (REALIZADA|RECIBIDA|INMEDIATA|EMITIDA|SEPA))*|PAGO DE ADEUDO DIRECTO SEPA|ADEUDO A SU CARGO|ADEUDO DE .*|RECIBOS?|ABONOS?|COMPRA|DEVOLUCI[OÓ]N.*|PAGO DE N[OÓ]MINAS.*|TRASPASO.*|COMISI[OÓ]N.*)$/i;
const NOMBRE_DELANTE = /^(.+?)\s+-\s+(TRANSFERENCIAS?|ADEUDO|PAGO|RECIBO|ABONO|COMPRA|DEVOLUCI|TRASPASO)/i;

const limpiarColaBanco = (texto) => (texto || '').trim()
  .replace(/^(?:\*{4,}\d{4}|\d{16})\s*/, '')
  .replace(/^N\s+\d{8,}\s*/i, '')
  .replace(/\s{2,}.*$/, '')
  .replace(/^[\s,.-]+|[\s,.-]+$/g, '');

export const tarjetaDelConcepto = (concepto) => {
  const c = concepto || '';
  const enmascarada = c.match(/\*{4,}(\d{4})\b/);
  if (enmascarada) return enmascarada[1];
  if (/TARJETA/i.test(c)) {
    const completa = c.match(/(?:^|[^\dN ]|[^N] )(\d{12})(\d{4})\b/);
    if (completa) return completa[2];
  }
  return '';
};

export const quienDelConcepto = (concepto) => {
  const c = (concepto || '').trim();
  if (!c) return { quien: '', detalle: '' };
  const partes = c.split('//').map((p) => p.trim()).filter(Boolean);
  if (partes.length < 2) return { quien: limpiarColaBanco(c), detalle: '' };
  const primera = partes[0];
  const cola = limpiarColaBanco(partes[partes.length - 1]);
  const colaGenerica = !cola || GENERICO_BANCO.test(cola);
  if (/PAGO DE N[OÓ]MINAS/i.test(c)) return { quien: 'Nóminas', detalle: colaGenerica ? '' : cola };
  const m = primera.match(NOMBRE_DELANTE);
  if (m) return { quien: m[1].trim(), detalle: colaGenerica ? '' : cola };
  if (/^TRANSFERENCIAS?\b/i.test(primera)) return { quien: '', detalle: colaGenerica ? '' : cola };
  if (!colaGenerica) return { quien: cola, detalle: '' };
  const adeudoDe = primera.match(/^ADEUDO DE (.+)$/i);
  if (adeudoDe && !GENERICO_BANCO.test(adeudoDe[1])) return { quien: adeudoDe[1].trim(), detalle: '' };
  return { quien: '', detalle: '' };
};

// Quién cobra + últimos 4 dígitos de la tarjeta + concepto escrito en la transferencia.
export const limpiarConcepto = (concepto, tarjetaDB = null) => {
  if (!concepto) return { proveedor: 'Sin concepto', tarjeta: tarjetaDB || '', detalle: '' };
  const { quien, detalle } = quienDelConcepto(concepto);
  const tarjeta = (tarjetaDB ? String(tarjetaDB).slice(-4) : '') || tarjetaDelConcepto(concepto);
  return {
    proveedor: quien || (detalle ? '' : 'Sin nombre en el banco'),
    tarjeta,
    detalle,
  };
};

// Formatear número como moneda
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0,00 €';
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Formatear número sin símbolo de moneda
export const formatNumberShort = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0,00';
  return new Intl.NumberFormat('es-ES', { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Categorías de gastos (personalizadas) - ordenadas alfabéticamente
export const CATEGORIAS_GASTO_DEFAULT = [
  { value: 'alimentacion', label: 'Alimentación', color: 'bg-orange-100 text-orange-800' },
  { value: 'alquileres', label: 'Alquileres', color: 'bg-fuchsia-100 text-fuchsia-800' },
  { value: 'arrendamiento', label: 'Arrendamiento', color: 'bg-violet-100 text-violet-800' },
  { value: 'combustible', label: 'Combustible', color: 'bg-amber-100 text-amber-800' },
  { value: 'comisiones_bancarias', label: 'Comisiones bancarias', color: 'bg-orange-100 text-orange-800' },
  { value: 'compras', label: 'Compras', color: 'bg-blue-100 text-blue-800' },
  { value: 'consumibles', label: 'Consumibles', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'devolucion', label: 'Devolución', color: 'bg-green-100 text-green-800' },
  { value: 'dietas', label: 'Dietas', color: 'bg-lime-100 text-lime-800' },
  { value: 'extras', label: 'Extras', color: 'bg-gray-100 text-gray-800' },
  { value: 'facturas', label: 'Facturas', color: 'bg-slate-100 text-slate-800' },
  { value: 'fianzas', label: 'Fianzas', color: 'bg-stone-100 text-stone-800' },
  { value: 'furgonetas', label: 'Furgonetas', color: 'bg-blue-100 text-blue-800' },
  { value: 'gasolina', label: 'Gasolina', color: 'bg-amber-100 text-amber-800' },
  { value: 'hacienda', label: 'Hacienda', color: 'bg-orange-100 text-orange-800' },
  { value: 'impuestos', label: 'Impuestos', color: 'bg-rose-100 text-rose-800' },
  { value: 'limpieza', label: 'Limpieza', color: 'bg-teal-100 text-teal-800' },
  { value: 'mantenimiento', label: 'Mantenimiento', color: 'bg-amber-100 text-amber-800' },
  { value: 'material', label: 'Material', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'multas', label: 'Multas', color: 'bg-red-100 text-red-800' },
  { value: 'nominas', label: 'Nóminas', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'otros', label: 'Otros', color: 'bg-gray-100 text-gray-800' },
  { value: 'parking', label: 'Parking', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'prestamos', label: 'Préstamos', color: 'bg-purple-100 text-purple-800' },
  { value: 'publicidad', label: 'Publicidad', color: 'bg-pink-100 text-pink-800' },
  { value: 'salarios', label: 'Salarios', color: 'bg-green-100 text-green-800' },
  { value: 'sanciones', label: 'Sanciones', color: 'bg-red-100 text-red-800' },
  { value: 'seguridad_social', label: 'Seguridad Social', color: 'bg-teal-100 text-teal-800' },
  { value: 'seguros', label: 'Seguros', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'servicios', label: 'Servicios', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'sin_justificar', label: 'Sin justificar', color: 'bg-red-100 text-red-800' },
  { value: 'suministros', label: 'Suministros', color: 'bg-sky-100 text-sky-800' },
  { value: 'transporte', label: 'Transporte', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'web_plataforma', label: 'Web y plataforma', color: 'bg-blue-100 text-blue-800' },
];

// Obtener color de categoría
export const getCategoriaColor = (categorias, categoria) => {
  const cat = categorias.find(c => c.value === categoria);
  return cat?.color || 'bg-gray-100 text-gray-800';
};

// Obtener label de categoría
export const getCategoriaLabel = (categorias, categoria) => {
  const cat = categorias.find(c => c.value === categoria);
  return cat?.label || categoria || 'Sin categoría';
};
